import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  EMPTY_DISCOVERY_FILTERS,
  type DiscoveryFilters,
  type SearchSort,
} from '@capsico/contracts';
import { searchOutlets } from '../api/discover';
import { pointKey, type DeliveryPoint } from './useDeliveryPoint';

export type FlagFilter = 'veg' | 'pureVeg' | 'rating4' | 'fast' | 'newOnly' | 'openNow';

/** Debounced text + filters + sort → search results for the delivery point. */
export function useSearch(point: DeliveryPoint | null, initialText = '') {
  const [text, setText] = useState(initialText);
  const [debounced, setDebounced] = useState(initialText);
  const [filters, setFilters] = useState<DiscoveryFilters>(EMPTY_DISCOVERY_FILTERS);
  const [sort, setSort] = useState<SearchSort>('relevance');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(text.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    setText(initialText);
    setDebounced(initialText.trim());
  }, [initialText]);

  const setCuisine = useCallback(
    (cuisine: string | null) => setFilters((current) => ({ ...current, cuisine })),
    [],
  );

  const active =
    debounced.length > 0 ||
    filters.cuisine !== null ||
    (Object.keys(filters) as Array<keyof DiscoveryFilters>).some(
      (key) => typeof filters[key] === 'boolean' && filters[key],
    );

  const query = useQuery({
    queryKey: ['search', pointKey(point), debounced, sort, filters],
    queryFn: () =>
      searchOutlets({
        point: { lat: (point as DeliveryPoint).lat, lng: (point as DeliveryPoint).lng },
        q: debounced,
        sort,
        filters,
      }),
    enabled: point !== null && active,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });

  return {
    text,
    setText,
    filters,
    toggle: (flag: FlagFilter) => setFilters((current) => ({ ...current, [flag]: !current[flag] })),
    setCuisine,
    clearFilters: () => setFilters(EMPTY_DISCOVERY_FILTERS),
    sort,
    setSort,
    active,
    loading: query.isLoading && active,
    failed: query.isError,
    retry: () => void query.refetch(),
    result: active ? (query.data ?? null) : null,
  };
}
